import { useState } from 'react'

import { Box, SearchField, Title } from '@factorialco/deprecated-design-system'
import {
  LegalEntityWithFinanceAccountsFragment,
  PayrollConceptPageDocument,
  PayrollConceptPageQuery,
} from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import Filter from 'modules/core/components/Filter'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { PayrollConceptRow } from 'modules/finance/components/Accounting/PayrollConcept/PayrollConceptRow'
import { NoAccountsBlankSlate } from 'modules/finance/pages/Accounting/VendorPage'
import PageLoading from 'modules/layout/components/Page/Loading'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

export type PayrollConceptLegalEntity =
  PayrollConceptPageQuery['companies']['legalEntities'][number]

const PayrollConceptPage = () => {
  const { search, onSearch } = useDebouncedSearch()
  const [legalEntity, setLegalEntity] = useState<LegalEntityWithFinanceAccountsFragment>()
  const [isFetching, setIsFetching] = useState(true)

  const { data, loading, error } = useQuery(PayrollConceptPageDocument, {
    variables: { legalEntityId: legalEntity?.id ?? '0' },
  })

  const canSeePayrollLedgerAccountSettings =
    data?.finance?._policies?.journalEntriesManagePurchaseInAccountsPayable?.can ?? false
  const canManage = data?.finance?._policies?.accountingManageAccountResources?.can ?? false
  const legalEntities = data?.companies?.legalEntities ?? []

  const payrollConcepts = (data?.payroll?.concepts ?? []).map((payrollConcept) => {
    return {
      ...payrollConcept,
      name: payrollConcept.label,
    }
  })

  useUnsafeEffect(() => {
    if (legalEntity) return
    setLegalEntity(legalEntities?.find((node) => node.main))
    setIsFetching(false)
  }, [legalEntities, legalEntity])

  const legalEntityOptions = useUnsafeMemo(
    () => legalEntities.map((le) => ({ label: le.legalName, value: le })),
    [legalEntities]
  )

  const accounts = legalEntity?.financeAccounts ?? []
  const filteredPayrollConcepts = sortBy(
    payrollConcepts.filter((payrollConcept) =>
      (payrollConcept.name ?? '').toLowerCase().includes(search.toLowerCase())
    ),
    ['name']
  )

  return (
    <ResponsivePage.Body
      loading={loading || isFetching}
      error={error}
      authorized={canSeePayrollLedgerAccountSettings}
    >
      <Box height="full">
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" paddingTop="s24">
          <>
            <Filter.SingleChoice
              label={i18n.t('finance.workspace.legal_entity_scope.selector_label')}
              options={legalEntityOptions}
              selectedOption={{ label: legalEntity?.legalName ?? '', value: legalEntity }}
              onValueChange={setLegalEntity}
              noFooter
            />
          </>
          {accounts.length > 0 && (
            <Box width="fit-content">
              <SearchField
                variant="regular"
                placeholder={i18n.t('common.search')}
                search={search}
                onSearch={onSearch}
              />
            </Box>
          )}
        </Box>

        {accounts.length === 0 &&
          (loading || isFetching ? (
            <Box flexGrow justifyContent="center">
              <PageLoading />
            </Box>
          ) : (
            <NoAccountsBlankSlate />
          ))}

        {accounts.length > 0 && legalEntity && (
          <>
            <Box
              flexDirection="row"
              gap="s16"
              justifyContent="spaceBetween"
              alignItems="flexStart"
              paddingTop="s64"
            >
              <Box width="s600">
                <Title title={i18n.t('finance.settings.accounting.payroll_concept_title')}></Title>
              </Box>
              <Box width="full">
                <Title title={i18n.t('finance.settings.accounting.assigned_accounts')}></Title>
              </Box>
              <Box width="s300">
                <Title title={i18n.t('finance.settings.accounting.balance_type')}></Title>
              </Box>
            </Box>

            <>
              {filteredPayrollConcepts.map((payrollConcept) => (
                <PayrollConceptRow
                  key={payrollConcept.id}
                  accounts={accounts}
                  legalEntity={legalEntity}
                  payrollConcept={payrollConcept}
                  canManage={canManage}
                />
              ))}
            </>
          </>
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default PayrollConceptPage
